// src/hooks/useLocutor.js - VERSÃO RETROCOMPATÍVEL
// ✅ Adiciona novas funcionalidades sem quebrar código existente
// ✅ Valores padrão para evitar erros
// 🔧 CORRIGIDO: Loop infinito de renderização

import { useState, useEffect, useRef, useCallback } from 'react';
import { SynchronizerLocutor } from '../components/synchronizers/SynchronizerLocutor';

export function useLocutor() {
  const [isScheduled, setIsScheduled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  
  // 🔥 NOVO: Estados opcionais com valores padrão
  const [scheduledTime, setScheduledTime] = useState(null);
  const [scheduledIn, setScheduledIn] = useState(null);
  const [musicInfo, setMusicInfo] = useState({
    currentTime: 0,
    duration: 0,
    remaining: 0
  });

  const syncLocutorRef = useRef(null);
  const updateInterval = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let statusInterval = null;

    const init = async () => {
      // Esperar AudioSyncCoordinator ficar pronto
      let attempts = 0;
      while (!window.audioCoordinator && attempts < 50) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 100));
        attempts += 1;
      }

      if (!isMounted) return;

      if (!window.audioCoordinator) {
        console.warn('[useLocutor] AudioCoordinator não disponível');
        return;
      }

      // Criar SynchronizerLocutor
      syncLocutorRef.current = new SynchronizerLocutor(window.audioCoordinator);
      setIsReady(true);
      console.log('[useLocutor] ✅ SyncLocutor criado');

      // Polling de status
      if (typeof syncLocutorRef.current.getStatus === 'function') {
        statusInterval = setInterval(() => {
          if (!syncLocutorRef.current) return;
          const status = syncLocutorRef.current.getStatus();
          if (!status) return;

          setIsScheduled(!!status.isScheduled);
          setIsPlaying(!!status.isPlaying);
        }, 500);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (statusInterval) {
        clearInterval(statusInterval);
      }
      if (syncLocutorRef.current) {
        try {
          syncLocutorRef.current.cancel?.();
          syncLocutorRef.current.destroy?.();
        } catch (e) {
          console.error('[useLocutor] Erro ao destruir SyncLocutor:', e);
        }
        syncLocutorRef.current = null;
      }
    };
  }, []);

  // 🔥 CORRIGIDO: Removido scheduledIn das dependências para evitar loop infinito
  useEffect(() => {
    if (!isScheduled) {
      setScheduledTime(null);
      setScheduledIn(null);
      if (updateInterval.current) {
        clearInterval(updateInterval.current);
        updateInterval.current = null;
      }
      return;
    }

    const updateTimeInfo = () => {
      // Tentar obter do SynchronizerLocutor primeiro
      if (syncLocutorRef.current && syncLocutorRef.current.getStatus) {
        const status = syncLocutorRef.current.getStatus();
        
        // Se o SynchronizerLocutor tem a informação, usar
        if (status && status.timeUntilLocutor !== undefined) {
          setScheduledIn(status.timeUntilLocutor);
          if (status.timeUntilLocutor > 0) {
            setScheduledTime(new Date(Date.now() + status.timeUntilLocutor * 1000));
          } else {
            setScheduledTime(null);
          }
        }
      }
      
      // Fallback: calcular localmente
      const audioElement = window.audioCoordinator?.audioElement || 
                          window.audioSync?.audioManager?.audioElement;
      
      if (audioElement && isFinite(audioElement.duration)) {
        const currentTime = audioElement.currentTime;
        const duration = audioElement.duration;
        const remaining = duration - currentTime;
        
        // Se não temos info do SynchronizerLocutor, estimar
        // 🔧 CORRIGIDO: Usar ref para evitar dependência cíclica
        if (syncLocutorRef.current && !syncLocutorRef.current.getStatus()?.timeUntilLocutor) {
          const LOCUTOR_DURATION_ESTIMATE = 10; // Estimativa padrão
          const MARGEM_APOS_LOCUTOR = 3;
          
          const tempoParaInicioLocutor = remaining - LOCUTOR_DURATION_ESTIMATE - MARGEM_APOS_LOCUTOR;
          
          if (tempoParaInicioLocutor > 0) {
            setScheduledIn(tempoParaInicioLocutor);
            setScheduledTime(new Date(Date.now() + tempoParaInicioLocutor * 1000));
          } else {
            setScheduledIn(0);
            setScheduledTime(null);
          }
        }
        
        setMusicInfo({
          currentTime,
          duration,
          remaining
        });
      }
    };

    // Atualizar imediatamente
    updateTimeInfo();
    
    // Atualizar periodicamente
    updateInterval.current = setInterval(updateTimeInfo, 500);
    
    return () => {
      if (updateInterval.current) {
        clearInterval(updateInterval.current);
        updateInterval.current = null;
      }
    };
  }, [isScheduled]); // 🔧 CORRIGIDO: Removido scheduledIn das dependências

  const scheduleLocutor = useCallback(async (audioUrl, duration, pauseMap = null, bedUrl = null) => {  // 🔥 NOVO: pauseMap + bedUrl
    const sync = syncLocutorRef.current;
    if (!sync) {
      console.warn('[useLocutor] scheduleLocutor chamado antes de inicializar');
      return false;
    }

    console.log(`[useLocutor] 🎭 scheduleLocutor pauseMap: ${pauseMap ? pauseMap.length + ' eventos' : 'não'}`);
    console.log(`[useLocutor] 🎵 scheduleLocutor bedUrl: ${bedUrl || 'não'}`);

    try {
      const result = await sync.scheduleLocutor(audioUrl, duration, pauseMap, bedUrl);  // 🔥 NOVO: passar pauseMap + bedUrl
      
      // Calcular tempo inicial se sucesso
      if (result !== false) {
        const audioElement = window.audioCoordinator?.audioElement || 
                            window.audioSync?.audioManager?.audioElement;
                            
        if (audioElement && isFinite(audioElement.duration)) {
          const remaining = audioElement.duration - audioElement.currentTime;
          const MARGEM_APOS_LOCUTOR = 3;
          const tempoParaInicioLocutor = remaining - duration - MARGEM_APOS_LOCUTOR;
          
          if (tempoParaInicioLocutor > 0) {
            setScheduledIn(tempoParaInicioLocutor);
            setScheduledTime(new Date(Date.now() + tempoParaInicioLocutor * 1000));
          }
        }
      }
      
      return result !== false;
    } catch (error) {
      console.error('[useLocutor] Erro ao agendar locutor:', error);
      return false;
    }
  }, []);

  const cancelLocutor = useCallback(() => {
    const sync = syncLocutorRef.current;
    if (!sync) return;

    try {
      sync.cancel();
      setScheduledTime(null);
      setScheduledIn(null);
    } catch (error) {
      console.error('[useLocutor] Erro ao cancelar locutor:', error);
    }
  }, []);

  const playLocutorNow = useCallback(async (audioUrl, pauseMap = null, bedUrl = null) => {  // 🔥 NOVO: pauseMap + bedUrl
    const sync = syncLocutorRef.current;
    if (!sync) {
      console.warn('[useLocutor] playLocutorNow chamado antes de inicializar');
      return false;
    }

    if (typeof sync.playLocutorNow !== 'function') {
      console.warn('[useLocutor] playLocutorNow não implementado no SynchronizerLocutor');
      return false;
    }

    console.log(`[useLocutor] 🎭 playLocutorNow pauseMap: ${pauseMap ? pauseMap.length + ' eventos' : 'não'}`);
    console.log(`[useLocutor] 🎵 playLocutorNow bedUrl: ${bedUrl || 'não'}`);

    try {
      const ok = await sync.playLocutorNow(audioUrl, pauseMap, bedUrl);  // 🔥 NOVO: passar pauseMap + bedUrl
      if (ok) {
        setScheduledTime(null);
        setScheduledIn(null);
      }
      return ok !== false;
    } catch (error) {
      console.error('[useLocutor] Erro no playLocutorNow:', error);
      return false;
    }
  }, []);

  const canSchedule = !!syncLocutorRef.current && isReady;

  return {
    isScheduled,
    isPlaying,
    scheduleLocutor,
    cancelLocutor,
    playLocutorNow,
    canSchedule,
    // 🔥 NOVO: Valores opcionais com defaults seguros
    scheduledTime: scheduledTime || null,
    scheduledIn: scheduledIn || null,
    musicInfo: musicInfo || { currentTime: 0, duration: 0, remaining: 0 }
  };
}